import type React from 'react'
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react'

export type TextSize = 'small' | 'medium' | 'large'

const TEXT_SIZE_OPTIONS: TextSize[] = ['small', 'medium', 'large']

interface TextSizeContextType {
	textSize: TextSize
	setTextSize: (size: TextSize) => void
}

const TextSizeContext = createContext<TextSizeContextType | undefined>(
	undefined,
)

export const TextSizeProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [textSize, setTextSize] = useState<TextSize>(() => {
		const saved = localStorage.getItem('textSize')
		if (saved && TEXT_SIZE_OPTIONS.includes(saved as TextSize)) {
			return saved as TextSize
		}
		return 'medium'
	})

	useEffect(() => {
		localStorage.setItem('textSize', textSize)
		const root = document.documentElement
		root.classList.remove('text-small', 'text-medium', 'text-large')
		if (textSize !== 'medium') {
			root.classList.add(`text-${textSize}`)
		}
	}, [textSize])

	return (
		<TextSizeContext.Provider value={{ textSize, setTextSize }}>
			{children}
		</TextSizeContext.Provider>
	)
}

export const useTextSize = () => {
	const context = useContext(TextSizeContext)
	if (context === undefined) {
		throw new Error('useTextSize must be used within a TextSizeProvider')
	}
	return context
}
